<template>
  <div class="app-container">
    <!-- 统计卡片 -->
    <el-row :gutter="16" class="stat-row" v-if="isAdmin">
      <el-col :xs="12" :sm="8" :md="4" v-for="item in chartData" :key="item.key">
        <el-card shadow="hover" class="stat-card" :body-style="{ padding: '16px' }">
          <div class="stat-card-inner">
            <div class="stat-info">
              <div class="stat-label">{{ item.label }}</div>
              <div class="stat-value" :style="{ color: item.color }">{{ item.value }}</div>
            </div>
            <div class="stat-icon" :style="{ background: item.color + '22', color: item.color }">
              <i :class="statusIcon(item.key)"></i>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="8" :md="4">
        <el-card shadow="hover" class="stat-card" :body-style="{ padding: '16px' }">
          <div class="stat-card-inner">
            <div class="stat-info">
              <div class="stat-label">订单总数</div>
              <div class="stat-value" style="color: #409eff;">{{ totalCount }}</div>
            </div>
            <div class="stat-icon" style="background: #409eff22; color: #409eff;">
              <i class="el-icon-s-data"></i>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 管理员：状态分布 -->
    <el-card v-if="isAdmin" class="panel-card" shadow="never" v-loading="loadingCounts">
      <div slot="header" class="card-header">
        <span>订单状态分布</span>
        <span class="card-meta">
          <el-button type="text" icon="el-icon-refresh" @click="fetchOrderStatusCounts">刷新</el-button>
          <span v-if="lastUpdated">更新时间：{{ lastUpdated }}</span>
        </span>
      </div>

      <el-alert v-if="countError" :title="countError" type="error" show-icon :closable="false" />

      <div v-else class="chart-body">
        <div class="donut-wrapper">
          <div class="donut" :style="{ background: donutGradient }">
            <div class="donut-center">
              <div class="center-number">{{ totalCount }}</div>
              <div class="center-label">订单总数</div>
            </div>
          </div>
        </div>
        <div class="chart-legend">
          <div v-for="item in chartData" :key="item.key + '-legend'" class="legend-item">
            <span class="legend-color" :style="{ background: item.color }"></span>
            <span class="legend-text">{{ item.label }}</span>
            <span class="legend-value">{{ item.value }}</span>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 普通用户欢迎 -->
    <el-card v-else class="panel-card" shadow="never">
      <div slot="header" class="card-header">
        <span>欢迎使用</span>
      </div>
      <div class="welcome-body">
        <img src="@/assets/logo.png" alt="订单管理系统" class="welcome-logo">
        <h3>欢迎使用订单管理系统</h3>
        <p>请按流程创建并提交订单，审核通过后可在历史订单中查看进度。</p>
        <el-button type="primary" icon="el-icon-plus" size="small" @click="$router.push('/order-form')">
          创建订单
        </el-button>
      </div>
    </el-card>

    <!-- 快捷入口 -->
    <el-card class="panel-card" shadow="never" style="margin-top: 10px;">
      <div slot="header" class="card-header">
        <span>快捷入口</span>
      </div>
      <el-row :gutter="16">
        <el-col :xs="12" :sm="8" :md="6" v-for="item in quickLinks" :key="item.path">
          <div class="quick-item" @click="$router.push(item.path)">
            <i :class="item.icon" :style="{ color: item.color }"></i>
            <span>{{ item.label }}</span>
          </div>
        </el-col>
      </el-row>
    </el-card>
  </div>
</template>

<script>
import request from '@/utils/request'

export default {
  name: 'OrderDashboard',
  data() {
    return {
      statusCounts: [],
      loadingCounts: false,
      countError: '',
      lastUpdated: ''
    }
  },
  computed: {
    userFromStorage() {
      try {
        const raw = localStorage.getItem('userData')
        return raw ? JSON.parse(raw) : null
      } catch (e) {
        return null
      }
    },
    userInfo() {
      const storeUser = (this.$store && this.$store.state && this.$store.state.user && this.$store.state.user.userInfo) || null
      return storeUser || (this.userFromStorage && this.userFromStorage.userInfo) || null
    },
    permission() {
      const storePerm = (this.$store && this.$store.state && this.$store.state.user && this.$store.state.user.permission)
      if (typeof storePerm !== 'undefined' && storePerm !== null) {
        return storePerm
      }
      return this.userFromStorage ? this.userFromStorage.permission : null
    },
    isAdmin() {
      const roleRaw = (this.userInfo && this.userInfo.role) || ''
      const role = roleRaw && roleRaw.toString().trim().toLowerCase()
      const perm = this.permission
      const isAdminRole = role === 'admin'
      const hasAdminPerm = perm === 1 || perm === '1' || (typeof perm === 'string' && perm.toLowerCase() === 'admin')
      return isAdminRole && hasAdminPerm
    },
    chartMeta() {
      return {
        0: { label: '待审核', color: '#e6a23c' },
        4: { label: '待补差价', color: '#409eff' },
        1: { label: '审核失败', color: '#f56c6c' },
        2: { label: '已完成', color: '#67c23a' },
        3: { label: '已作废', color: '#909399' }
      }
    },
    chartData() {
      return Object.keys(this.chartMeta).map(key => {
        const numKey = Number(key)
        const matched = this.statusCounts.find(item => Number(item.order_status) === numKey)
        return {
          key: numKey,
          label: this.chartMeta[key].label,
          color: this.chartMeta[key].color,
          value: matched ? matched.sum : 0
        }
      })
    },
    totalCount() {
      return this.chartData.reduce((sum, item) => sum + item.value, 0)
    },
    donutGradient() {
      if (this.totalCount === 0) return '#ebeef5'
      const stops = []
      let current = 0
      this.chartData.forEach(item => {
        const portion = (item.value / this.totalCount) * 100
        if (portion <= 0) return
        const start = current
        const end = current + portion
        stops.push(`${item.color} ${start}% ${end}%`)
        current = end
      })
      return `conic-gradient(${stops.join(', ')})`
    },
    quickLinks() {
      const links = [
        { label: '创建订单', path: '/order-form', icon: 'el-icon-document-add', color: '#409eff' }
      ]
      if (this.isAdmin) {
        links.push(
          { label: '订单管理', path: '/admin-dashboard', icon: 'el-icon-s-check', color: '#67c23a' },
          { label: '产品管理', path: '/product-management', icon: 'el-icon-goods', color: '#e6a23c' },
          { label: '系统配置管理', path: '/config-management', icon: 'el-icon-setting', color: '#909399' },
          { label: '招商账号', path: '/agent-accounts', icon: 'el-icon-user', color: '#409eff' }
        )
      } else {
        links.push(
          { label: '历史订单', path: '/history-orders', icon: 'el-icon-time', color: '#67c23a' }
        )
      }
      return links
    }
  },
  mounted() {
    if (this.isAdmin) {
      this.fetchOrderStatusCounts()
    }
  },
  methods: {
    statusIcon(key) {
      const map = {
        0: 'el-icon-time',
        1: 'el-icon-circle-close',
        2: 'el-icon-circle-check',
        3: 'el-icon-remove-outline',
        4: 'el-icon-wallet'
      }
      return map[key] || 'el-icon-s-data'
    },
    async fetchOrderStatusCounts() {
      this.loadingCounts = true
      this.countError = ''
      try {
        const result = await request.get('/api/order/count')
        this.statusCounts = Array.isArray(result) ? result : []
        this.lastUpdated = new Date().toLocaleString()
      } catch (error) {
        this.countError = (error && error.message) || '获取订单状态统计失败'
      } finally {
        this.loadingCounts = false
      }
    }
  },
  watch: {
    isAdmin(newVal, oldVal) {
      if (newVal && !oldVal && !this.statusCounts.length && !this.loadingCounts) {
        this.fetchOrderStatusCounts()
      }
    }
  }
}
</script>

<style scoped>
.app-container {
  width: 100%;
}

.stat-row {
  margin-bottom: 10px;
}

.stat-card {
  margin-bottom: 10px;
}

.stat-card-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.stat-label {
  font-size: 13px;
  color: #909399;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.panel-card {
  margin-bottom: 0;
}

.panel-card >>> .el-card__header {
  padding: 12px 16px;
  border-bottom: 1px solid #ebeef5;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.card-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: normal;
  color: #909399;
}

.chart-body {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 48px;
  padding: 20px 10px;
  flex-wrap: wrap;
}

.donut {
  width: 180px;
  height: 180px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.donut-center {
  width: 110px;
  height: 110px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.center-number {
  font-size: 28px;
  font-weight: 700;
  color: #303133;
}

.center-label {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.chart-legend {
  min-width: 180px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px dashed #ebeef5;
}

.legend-color {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
}

.legend-text {
  flex: 1;
  font-size: 13px;
  color: #606266;
}

.legend-value {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.welcome-body {
  text-align: center;
  padding: 40px 20px;
}

.welcome-logo {
  width: 72px;
  height: 72px;
  object-fit: contain;
  margin-bottom: 12px;
}

.welcome-body h3 {
  margin: 0 0 8px;
  font-size: 18px;
  color: #303133;
  font-weight: 600;
}

.welcome-body p {
  margin: 0 0 20px;
  color: #909399;
  font-size: 14px;
}

.quick-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px 12px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 10px;
  background: #fff;
}

.quick-item:hover {
  border-color: #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.15);
}

.quick-item i {
  font-size: 28px;
}

.quick-item span {
  font-size: 13px;
  color: #606266;
}
</style>
